import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Scroll-reveal animation for the Tailwind pages.
// Render <ScrollReveal /> once on a page: it finds headings, text, cards and images inside `main.tw`
// and fades/slides them in when they scroll into view (styles live in src/tailwind.css → [data-reveal]).
// Cards in the same grid come in one after another. Skipped entirely for users who prefer reduced motion.

const TARGETS = [
  "main.tw section h1",
  "main.tw section h2",
  "main.tw section h1 ~ p",
  "main.tw section h2 ~ p",
  "main.tw section h2 ~ a",
  "main.tw section h1 ~ div",
  "main.tw section .grid > *",
  "main.tw section ul > li",
  "main.tw section img",
].join(",");

// Things that already animate, live inside sliders, or are tiny inline icons are left alone.
const SKIP = ".swiper, .snap-x, [class*='animate-'], form *";

const ScrollReveal = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    // Fresh selection every run (React StrictMode runs effects twice). Elements nested inside another
    // target are skipped so the same content never animates twice.
    // On phones, cards inside a horizontal swiper (.m-swipe) are off-screen sideways, so they are not animated.
    const skip = window.matchMedia("(max-width: 639.98px)").matches ? `${SKIP}, .m-swipe` : SKIP;
    const all = [...document.querySelectorAll(TARGETS)].filter((el) => !el.closest(skip));
    const set = new Set(all);
    const els = all.filter((el) => {
      for (let p = el.parentElement; p; p = p.parentElement) if (set.has(p)) return false;
      return !el.classList.contains("reveal-in");
    });

    els.forEach((el) => {
      // stagger siblings (e.g. cards in a grid): 70ms apart, max 0.5s
      const siblings = [...el.parentElement.children].filter((c) => set.has(c));
      const i = Math.max(0, siblings.indexOf(el));
      el.style.setProperty("--reveal-delay", `${Math.min(i * 70, 500)}ms`);
      el.setAttribute("data-reveal", "");
    });

    const show = (el) => el.classList.add("reveal-in");
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        fired = true;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            show(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));

    // Safety net: if the observer never reports (very old/odd browsers), show everything instead of
    // leaving content invisible.
    const fallback = setTimeout(() => {
      if (!fired) els.forEach(show);
    }, 2500);

    return () => {
      clearTimeout(fallback);
      io.disconnect();
    };
  }, [pathname]);

  return null;
};

export default ScrollReveal;
